import * as Sentry from '@sentry/browser'
import router from '@/router'

import {
  SETTINGS_KEYS
} from '@/utils/keys'
import setWidgets from '@/utils/setWidgets'
import {
  API_GET,
  API_POST,
  API_UPLOAD
} from '@/utils/http'

function hydrateUI (commit, response, initial = false) {
  const {
    shopObj,
    previewItems,
    amptheme,
    settings,
    plan,
    eligibleForTrial,
    setup,
    memberSince,
    selfUninstalled,

    vFeatures,
    vKraken,
    vAuto
  } = response

  commit('SET_SELF_UNINSTALLED', selfUninstalled)
  commit('SET_VFEATURES', vFeatures)

  if (vKraken) {
    commit('SET_VKRAKEN', vKraken)
  }

  if (vAuto) {
    commit('SET_VAUTO', vAuto)
  }

  commit('SET_PLAN', plan)
  commit('SET_TRIAL_ELIGIBILITY', eligibleForTrial)
  commit('SET_PREVIEW_ITEMS', previewItems)

  if (amptheme) {
    commit('SET_AMPTHEME', amptheme)
  }

  if (settings) {
    commit('SET_SETTINGS', settings)
  }

  commit('SET_MEMBER_SINCE', memberSince)

  if (initial) {
    setWidgets(shopObj, plan)
  }

  return {
    setup,
    plan,
    shopObj
  }
}

export default {
  // v2
  async getEvents ({ commit }) {
    const data = await API_GET('/analytics/events', { _: 1 })
    commit('SET_EVENTS', data)
  },
  async getStatusMaster ({ commit, state }) {
    return API_GET('/client/status')
  },
  async getStatus ({ commit, state, dispatch }) {
    const response = await dispatch('wrapApi', 'getStatusMaster')
    if (response === null) return

    return response
  },
  async getClientMaster ({ commit }) {
    return hydrateUI(commit, await API_GET('/client'), true)
  },
  async getClient ({ commit, dispatch }) {
    const response = await dispatch('wrapApi', 'getClientMaster')
    if (response === null) return

    const { setup, plan, shopObj } = response

    if (setup && plan) {
      // stop loading & go to home
      commit('SET_SHOPOBJ', shopObj)

      if (['onboarding', 'login'].includes(router.currentRoute.name)) {
        router.replace({ name: 'home' })
      }
    } else {
      if (!(router.currentRoute.path.startsWith('/setup') || router.currentRoute.name === 'onboarding')) {
        router.replace({ name: 'onboarding' })
      }

      // stop loading & show onboarding
      commit('SET_SHOPOBJ', shopObj)
    }

    return response
  },
  async submitRequest ({ commit, dispatch }, { type, krakenShop }) {
    try {
      await API_POST('/process', { type, krakenShop })

      return true
    } catch (e) {
      if (e.response && e.response.status === 429) {
        return e.response.data.error
      } else {
        throw e
      }
    }
  },

  async wrapApi ({ commit, dispatch }, name) {
    commit('LOADING', true)

    try {
      const response = await dispatch(name)

      commit('LOADING', false)

      return response
    } catch (e) {
      if (e.response && e.response.status === 401) {
        if (router.currentRoute.name !== 'login') {
          router.replace({ name: 'login' })
        }

        commit('LOADING', false)

        return null
      } else {
        commit('SET_ERROR')
        commit('LOADING', false)
        throw e
      }
    }
  },

  handleLogoHeight ({ commit, state }, event) {
    const height = event.target.value || '0px'
    const newHeight = parseInt(height.replace('px', ''))
    const newWidth = newHeight * state.logoAspectRatio

    commit('UPDATE_AMPTHEME', {
      key: 'logo',
      value: {
        width: newWidth + 'px',
        height: newHeight + 'px'
      }
    })
  },
  async savePlan ({ commit, state }, plan) {
    commit('SAVING')

    const response = await API_POST('/plan', { plan })

    if (response.redirectTo) {
      window.location = response.redirectTo
    } else {
      const oldPlan = state.plan

      commit('SET_VFEATURES', response.vFeatures)
      commit('SET_PLAN', plan)

      router.replace({
        name: oldPlan ? 'account_plan' : 'setup_done',
        query: {
          plan,
          subscription: 'success'
        }
      })

      commit('SAVING', false)
    }
  },
  async saveMaster ({ commit, state, getters }, setup = false) {
    commit('SAVING')

    const update = {}
    const updateKeys = Object.keys(state.previewChange).filter(key => state.previewChange[key])

    const newLogo = state.settings.language.logo_asset_version === true

    if (newLogo) {
      const name = 'logo'
      const ext = state.settings.language.logo_asset_ext
      const mime = state.settings.language.logo_asset_mime
      const base64 = state.picture

      if (!mime || !ext) {
        Sentry.withScope(scope => {
          scope.setTag('name', name)
          scope.setTag('ext', ext)
          scope.setTag('mime', mime)
          scope.setExtra('base64', base64 ? base64.substring(0, 50) : null)
          Sentry.captureException(new Error('Invalid picture to upload'))
        })
      }

      const { url } = await API_POST('/client/imageUploadUrl', {
        name: `${name}.${ext}`,
        contentType: mime
      })

      const base64Str = base64.substring(base64.indexOf(',') + 1)
      const buff = Buffer.from(base64Str, 'base64')

      await API_UPLOAD(url, buff, mime)
    }

    if (setup && !updateKeys.includes('amptheme')) {
      updateKeys.push('amptheme')
    }

    if (updateKeys.length !== 0) {
      update.settings = {}

      if (updateKeys.includes('amptheme')) {
        update.auto = getters.ampCssAndFonts
        update.amptheme = state.amptheme
      }

      SETTINGS_KEYS.forEach(skey => {
        if (updateKeys.includes(skey)) {
          update.settings[skey] = state.settings[skey]
        }
      })
    }

    if (setup) update.setup = true

    hydrateUI(commit, await API_POST('/client/config', { update }))

    commit('SAVING', false)
  },
  async changePreviewItem ({ commit, state }, { type, item }) {
    commit('SAVING')

    await API_POST('/client/previewItem', { type, item })
    commit('UPDATE_PREVIEW_ITEMS', { type, item })

    commit('SAVING', false)
  }
}
